/**
 * AgentModeSwitcher — Mode selector for Text, Voice, SMS, Call
 */
import type { InteractionMode } from '@/lib/api/agent'
import { useHasPermission } from '@/stores/authStore'

interface AgentModeSwitcherProps {
  mode: InteractionMode
  onModeChange: (mode: InteractionMode) => void
}

function ModeButton({ value, label, icon, requiredPermission, isActive, onSelect }: {
  value: InteractionMode; label: string; icon: string; requiredPermission?: string; isActive: boolean; onSelect: () => void
}) {
  const hasPermission = requiredPermission ? useHasPermission(requiredPermission) : true

  return (
    <button
      onClick={() => hasPermission && onSelect()}
      disabled={!hasPermission}
      title={!hasPermission ? `Requires ${requiredPermission} permission` : label}
      aria-label={`Switch to ${label} mode`}
      className={`rounded px-2 py-1 text-xs transition-colors ${
        isActive
          ? 'bg-primary text-primary-foreground'
          : !hasPermission
            ? 'cursor-not-allowed opacity-40'
            : 'hover:bg-muted'
      }`}
    >
      {icon}
    </button>
  )
}

const MODES: { value: InteractionMode; label: string; icon: string; requiredPermission?: string }[] = [
  { value: 'text', label: 'Text', icon: '💬' },
  { value: 'voice_browser', label: 'Voice', icon: '🎤' },
  { value: 'sms', label: 'SMS', icon: '📱', requiredPermission: 'phone.send_sms' },
  { value: 'voice_twilio', label: 'Call', icon: '📞', requiredPermission: 'phone.make_calls' },
]

export function AgentModeSwitcher({ mode, onModeChange }: AgentModeSwitcherProps) {
  return (
    <div className="flex gap-1">
      {MODES.map(m => (
        <ModeButton
          key={m.value}
          value={m.value}
          label={m.label}
          icon={m.icon}
          requiredPermission={m.requiredPermission}
          isActive={mode === m.value}
          onSelect={() => onModeChange(m.value)}
        />
      ))}
    </div>
  )
}
